import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../comun/services/grpc/mapConnectError";
import { createCachedFactory } from "../../../utils/factoryUtils";
import {
  AplicacionRecipienteItem,
  AplicacionRecipienteService,
} from "../../../generated/grpc/aplicaciones_pb";
import { grpcAplicacionRecipienteDatabase } from "../database/GrpcAplicacionRecipienteDatabase";
import { serviceSync } from "../../../base/services/ServiceSync";
import { AplicacionRecipienteResponse } from "../response/AplicacionRecipienteResponse";

/** Recipientes (canecas, tanques) con los que se mide una aplicación manual:
 *  los del cliente, por delta. En el primer listado de un cliente que nunca
 *  tuvo ninguno, el server le siembra sus copias de los dos tamaños estándar y
 *  vienen en esa misma respuesta.
 *
 *  Ordena por **litros y después por nombre**: en el selector del formulario se
 *  busca por tamaño ("la de 200"), no alfabéticamente, y así "Caneca 200 L"
 *  nunca queda debajo de "Tanque 600 L" por una T. El server no ordena —
 *  manda solo lo que cambió. */
class AplicacionRecipienteGrpcServiceCliente {
  constructor(private readonly _clienteId: string) {}

  async getListAsync(): Promise<AplicacionRecipienteResponse[]> {
    await this._syncAsync();
    const items =
      await grpcAplicacionRecipienteDatabase.getListAsync<AplicacionRecipienteItem>();
    return items
      .map(toAplicacionRecipienteResponse)
      // El store guarda lo que trajo el delta de este cliente, pero se filtra
      // igual: la base local sobrevive a un cambio de sesión en el mismo
      // navegador, y sin esto se mezclarían los recipientes de dos clientes.
      .filter((r) => r.clienteId === this._clienteId)
      .sort((a, b) => a.litros - b.litros || a.nombre.localeCompare(b.nombre));
  }

  clearCache(): void {
    serviceSync.reset(`grpc:aplicacion-recipientes:${this._clienteId}`);
  }

  private async _syncAsync(): Promise<void> {
    await serviceSync.run(
      `grpc:aplicacion-recipientes:${this._clienteId}`,
      async () => {
        const ultimaSincronizacion =
          await grpcAplicacionRecipienteDatabase.getUltimaSincronizacionAsync(
            this._clienteId,
          );
        const client = createClient(
          AplicacionRecipienteService,
          grpcTransportProvider.get(),
        );

        let response;
        try {
          response = await client.listByClienteId({
            clienteId: this._clienteId,
            ultimaSincronizacion,
          });
        } catch (error) {
          throw mapConnectError(error);
        }

        await grpcAplicacionRecipienteDatabase.putDeltaAsync(
          this._clienteId,
          response.items,
          response.timestampSincronizacion,
        );
      },
    );
  }
}

/** Factory cacheada: una única instancia por clienteId (comparte
 *  cooldown/caché, mismo criterio que `fx_createTareaGrpcService`). */
export const fx_createAplicacionRecipienteGrpcService = createCachedFactory(
  (clienteId: string) => new AplicacionRecipienteGrpcServiceCliente(clienteId),
);

/** Adapta un `AplicacionRecipienteItem` (gRPC) al shape
 *  `AplicacionRecipienteResponse`. Exportada porque los servicios de guardado y
 *  borrado también la necesitan. */
export function toAplicacionRecipienteResponse(
  recipiente: AplicacionRecipienteItem,
): AplicacionRecipienteResponse {
  return {
    id: recipiente.id,
    isEliminado: recipiente.eliminado,
    clienteId: recipiente.clienteId,
    nombre: recipiente.nombre,
    litros: recipiente.litros,
  };
}
